<template>
  <div
    class="relative mb-8"
    :class="{ 'full-width': desktopDisplayMode === 'fullScreen' }"
    :data-ab-id="dataAbId"
  >
    <SfScrollable
      ref="scrollable"
      :active-index="activeIndex"
      direction="horizontal"
      buttons-placement="floating"
      is-active-index-centered
      class="pointer-events-auto relative size-full snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      @on-scroll="updateActiveIndex"
      @on-prev="
        ({ preventDefault }) => {
          preventDefault();
          setActiveIndex((activeIndex - 1 >= 0 ? activeIndex - 1 : carouselItems.length - 1));
        }
      "
      @on-next="
        ({ preventDefault }) => {
          preventDefault();
          setActiveIndex((activeIndex + 1) % carouselItems.length);
        }
      "
    >
      <template #previousButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :disabled="false"
          class="absolute left-0 z-[2] ml-3 flex !rounded-full"
          variant="secondary"
          size="sm"
          square
        >
          <SfIconChevronLeft size="sm" />
        </KikButton>
      </template>
      <template #nextButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :disabled="false"
          class="absolute right-0 z-[2] mr-3 flex !rounded-full"
          variant="secondary"
          size="sm"
          square
        >
          <SfIconChevronRight size="sm" />
        </KikButton>
      </template>
      <div
        v-for="(item, index) in carouselItems"
        :key="index"
        class="m-0 flex aspect-[1/1.1970] w-full shrink-0 grow basis-full snap-center flex-col justify-end desktop:aspect-auto desktop:h-[500px]"
        :class="{ 'desktop:flex-row': item.desktopAssetPosition === 'left', 'desktop:flex-row-reverse': item.desktopAssetPosition === 'right' }"
        @mousedown="stopAutoplay"
        @mouseup="startAutoplay"
        @mouseover="stopAutoplay"
        @mouseleave="startAutoplay"
        @touchstart="stopAutoplay"
        @touchend="startAutoplay"
      >
        <div
          v-if="item.asset.type === 'media'"
          class="size-full"
        >
          <MyKikBadge
            v-if="item.myKikBadge?.[0]"
            :badge="item.myKikBadge[0]"
          />
          <video
            preload="metadata"
            class="absolute inset-y-0 size-full object-cover"
            :class="{ 'desktop:relative desktop:w-2/4': item.desktopAssetPosition !== 'fullWidth' }"
            :muted="item.asset.muted"
            :autoplay="item.asset.enableAutoplay"
            controls
            :controlsList="item.asset.controlsList"
            :disablePictureInPicture="item.asset.disablePicInPic"
            :playsinline="item.asset.inlineOnly"
          >
            <source
              :src="item.asset.src"
              type="video/mp4"
            >
          </video>
        </div>
        <SharedLink
          v-if="item.asset.type === 'image'"
          :link="item.asset?.link"
          class="absolute inset-y-0 size-full"
          :class="{ 'desktop:relative desktop:w-2/4': item.desktopAssetPosition !== 'fullWidth' }"
        >
          <div
            v-if="item.asset.type === 'image'"
            class="size-full"
          >
            <SharedMyKikBadge
              v-if="item.myKikBadge?.[0]"
              :badge="item.myKikBadge[0]"
            />
            <NuxtImg
              :src="item.asset.src"
              :alt="item.asset.alt"
              sizes="lg:100vw 48vw"
              :srcset="item.asset.srcset"
              :width="item.asset.dimensions.width"
              :height="item.asset.dimensions.height"
              :loading="imageLoadType"
              :preload="imageLoadType === 'eager'"
              :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
              class="size-full object-cover"
            />
          </div>
        </SharedLink>
        <div
          class="relative z-[1] mb-[6px] flex flex-col justify-end p-3 pb-6 desktop:px-14 desktop:py-10"
          :class="{ 'background-filter': item.asset.type === 'image' && item.desktopAssetPosition === 'fullWidth',
                    'mobile:background-filter mobile:!bg-transparent': item.mobileGradient,
                    '!mb-16': item.asset.type === 'media' && item.desktopAssetPosition === 'fullWidth',
                    'desktop:w-2/4': item.desktopAssetPosition !== 'fullWidth' }"
          :style="{ backgroundColor: `${item.backgroundColor}`, alignItems: `${isMobile ? 'left' : item.contentAlignment}` }"
        >
          <div
            class="flex items-center gap-1"
            :style="{ color: `${item.titleColor}` }"
          >
            <p class="z-[1] mb-1 text-[24px] font-bold leading-[28px] desktop:text-[40px] desktop:leading-[46px]">
              {{ item.title }}
              <span
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'title'"
                class="z-[1] ml-1 inline-flex cursor-pointer align-middle"
                tabindex="0"
                @click="openLegal(item)"
                @touchstart="openLegal(item)"
                @keydown="(e: KeyboardEvent) => keyHandler(e, [], () => isLegalOpen = true)"
              ><SfIconInfo
                size="base"
              /></span>
            </p>
          </div>
          <div
            class="z-[1] flex items-center gap-1 text-xl"
            :style="{ color: `${item.subtitleColor}` }"
          >
            <p>
              {{ item.subTitle }}
              <span
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'subtitle'"
                class="ml-1 inline-flex cursor-pointer align-middle"
                tabindex="0"
                @click="openLegal(item)"
                @touchstart="openLegal(item)"
                @keydown="(e: KeyboardEvent) => keyHandler(e, [], () => isLegalOpen = true)"
              ><SfIconInfo
                size="base"
              /></span>
            </p>
          </div>
          <div
            v-if="item.links.length"
            class="z-[1] mt-4 flex flex-wrap gap-2"
          >
            <SharedLink
              v-for="(link, i) in item.links"
              :key="`link-${i}`"
              :link="link.link"
              class="rounded-md border px-4 py-2 text-center mobile:!text-base"
              :style="{
                color: `${link.ctaTextColor}`,
                backgroundColor: `${link.buttonColor}`,
                borderColor: `${link.borderColor}`,
              }"
            >
              {{ link.ctaText }}
            </SharedLink>
          </div>
        </div>
        <KikLegalInformationDrawer
          :legal-text="currentLegalText"
          :title="currentLegalTitle"
          :open-button="false"
          :is-open="isLegalOpen"
          @on-visibility-change="isLegalOpen = $event"
        />
      </div>
    </SfScrollable>
    <div class="absolute bottom-0 mt-1 flex w-full gap-1 bg-white pt-0.5">
      <p
        v-for="({}, i) in items"
        :key="`dot-${i}`"
        class="h-1 w-full bg-neutral-200"
        :class="{
          '!bg-primary-700': activeIndex === i,
        }"
        @click="setActiveIndex(i)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SfScrollable, SfIconChevronLeft, SfIconChevronRight, SfIconInfo } from '@storefront-ui/vue';
import type { SfScrollableOnScrollData } from '@storefront-ui/vue';
import magnoliaGetters from '~/composables/getters/magnoliaGetters';
import keyHandler from '~/helpers/a11y/keyHandler';
import { useSwipe } from '@vueuse/core';

const scrollable = ref<HTMLElement>();
useSwipe(scrollable, {
  onSwipeEnd(event, direction) {
    if (direction === 'left') {
      setActiveIndex((activeIndex.value + 1) % carouselItems.value.length);

      return;
    }

    setActiveIndex((activeIndex.value - 1 >= 0 ? activeIndex.value - 1 : carouselItems.value.length - 1));
  },
});

interface Props {
  autoplay?: boolean;
  autoplayTime?: string;
  desktopDisplayMode?: string;
  items?: any[];
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: false,
  autoplayTime: '5',
  items: () => [],
  imageLoadType: 'lazy',
});

const { autoplay, autoplayTime, items, desktopDisplayMode } = toRefs(props);
const { $domain } = useNuxtApp();
const { isMobile } = useDevice();
const activeIndex = ref(0);
const interval = ref();
const isLegalOpen = ref(false);
const currentLegalText = ref('');
const currentLegalTitle = ref('');

const carouselItems = computed(() => {
  return items.value?.map((item) => {
    let asset = {};
    if (item.assetMode?.field === 'image') {
      asset = {
        type: item.assetMode.field,
        src: magnoliaGetters.getDefaultSrcAsset(item.assetMode.image, $domain.cmsAssetDomainPath),
        srcset: magnoliaGetters.getSrcSetImagesAsString(item.assetMode.image, $domain.cmsAssetDomainPath, item.assetMode.aspectRatio, item.assetMode.mobileAspectRatio?.[0]?.aspectRatio, item.assetMode.mobileImage?.[0]?.image),
        link: item.assetMode.imageLink?.[0] ? magnoliaGetters.getLinkAndName(item.assetMode.imageLink[0], $domain.cmsAssetDomainPath, item.assetMode.imageLink[0].switchable.intLinkParam) : {},
        title: magnoliaGetters.getTitleImage(item.assetMode.image, item.assetMode.imageTitle),
        alt: magnoliaGetters.getAltImage(item.assetMode.image, item.assetMode.alt),
        dimensions: magnoliaGetters.getImageDimensions(item.assetMode.image),
      };
    }
    if (item.assetMode?.field === 'media') {
      const deviceMedia = isMobile && item.assetMode.mobileMedia?.[0] ? item.assetMode.mobileMedia[0].media : item.assetMode.media;
      asset = {
        type: item.assetMode.field,
        src: `${magnoliaGetters.getDefaultSrcAsset(deviceMedia, $domain.cmsAssetDomainPath)}#t=0.2`,
        muted: item.assetMode.muted,
        controlsList: magnoliaGetters.getControlsList(item.assetMode),
        disablePicInPic: item.assetMode.disablePicInPic,
        inlineOnly: item.assetMode.inlineOnly,
        enableAutoplay: item.assetMode.enableAutoplay,
      };
    }

    const links = item.ctaLink?.length
      ? item.ctaLink.map((cta: { ctaText: any; ctaTextColor: number | undefined; buttonColor: number | undefined; borderColor: number | undefined; switchable: { intLinkParam: string | undefined } }) => ({
          ctaText: cta.ctaText,
          ctaTextColor: magnoliaGetters.getHexColor(cta.ctaTextColor),
          buttonColor: magnoliaGetters.getHexColor(cta.buttonColor),
          borderColor: magnoliaGetters.getHexColor(cta.borderColor),
          link: magnoliaGetters.getLinkAndName(cta, $domain.cmsAssetDomainPath, cta.switchable.intLinkParam),
        }))
      : [];

    return {
      ...item,
      titleColor: magnoliaGetters.getHexColor(item.titleColor),
      subtitleColor: magnoliaGetters.getHexColor(item.subtitleColor),
      backgroundColor: item.desktopAssetPosition !== 'fullWidth' ? magnoliaGetters.getHexColor(item.backgroundColor) : undefined,
      links,
      asset,
    };
  });
});

const openLegal = (item: any) => {
  currentLegalText.value = item.legalText;
  currentLegalTitle.value = item.legalTitle;
  isLegalOpen.value = true;
};

watch(() => isLegalOpen.value, () => {
  if (isLegalOpen.value) {
    stopAutoplay();

    return;
  }

  startAutoplay();
});

const isClickEvent = ref(false);

const setActiveIndex = (index: number) => {
  isClickEvent.value = true;
  if (index < 0 || index > carouselItems.value?.length) {
    return;
  }
  activeIndex.value = index;
  setTimeout(() => {
    isClickEvent.value = false;
  }, 500);
  startAutoplay();
};

const updateActiveIndex = (event: SfScrollableOnScrollData) => {
  if (isClickEvent.value) {
    return;
  }
  const itemWidth = event.scrollWidth / carouselItems.value.length;
  const newActiveIndex = Math.round(event.left / itemWidth);
  activeIndex.value = newActiveIndex < 0 ? 0 : newActiveIndex;
  startAutoplay();
};

const startAutoplay = () => {
  if (isLegalOpen.value) {
    return;
  }

  if (autoplay.value) {
    clearInterval(interval.value);
    interval.value = setInterval(() => {
      setActiveIndex((activeIndex.value + 1) % carouselItems.value.length);
    }, (Number(autoplayTime.value) * 1000));
  }
};

const stopAutoplay = () => {
  clearInterval(interval.value);
};

onMounted(() => {
  startAutoplay();
});
onUnmounted(() => {
  clearInterval(interval.value);
});
</script>
